// wizardStepStatus — tri-state visual model for wizard step indicators.
//
// AntD <Steps> status only supports wait/process/finish/error.  AMBER is
// applied via icon COLOR only; the AntD status prop is not changed.
//
// Visual model:
//   complete   (#52c41a) — step in completedSteps AND has data (done + verified)
//   skipped    (#faad14) — OPTIONAL step in completedSteps but hasData=false
//   current    (#1890ff) — the step the user is on (blue, unchanged)
//   incomplete (#d9d9d9) — not done / required step missing / not yet reached

import { WizardStep } from "./wizardInvalidation";

/** Indices of wizard steps that are optional (may be intentionally skipped).
 *
 * NOTE: `StepReview.tsx` also declares a local, differently-named-in-spirit
 * `OPTIONAL_STEPS` array for its own publish-warning purposes — it is a
 * `{step, label}[]` with DIFFERENT membership (includes Users, excludes
 * Debounce), not this `ReadonlySet<number>`. They serve different concerns
 * (this one gates navigation friction; StepReview's gates a dismissible
 * publish warning) and are not interchangeable — importing the wrong one
 * changes behavior, not just syntax. */
export const OPTIONAL_STEPS: ReadonlySet<number> = new Set([
  WizardStep.ChannelModifiers,
  WizardStep.Debounce,
  WizardStep.Bacnet,
  WizardStep.Notifications,
  WizardStep.Actions,
  WizardStep.MonitorGrid,
]);

/**
 * Compose a completion predicate for navigation-gating purposes: a step
 * counts as satisfied if it's genuinely complete, OR if it's an
 * `OPTIONAL_STEPS` member. Optional steps auto-complete the moment the
 * operator visits them and are only ever a dismissible warning on Review,
 * never a hard publish gate — so requiring them complete before a
 * breadcrumb jump would add navigation friction with no correctness
 * benefit, and would reopen a real gap for any step (like Channel
 * Modifiers) that a hydration path doesn't auto-cascade.
 *
 * Used by `SetupWizardPage`'s forward-jump guard together with
 * `wizardCompletion.ts`'s `firstGapBefore`; kept here, next to
 * `OPTIONAL_STEPS`, so the two stay in sync and the composition is
 * unit-testable without React.
 */
export function stepSatisfiedForNavigation(
  isStepCompleted: (step: number) => boolean,
): (step: number) => boolean {
  return (step: number) => OPTIONAL_STEPS.has(step) || isStepCompleted(step);
}

/** The four visual states a wizard step can be in. */
export type StepVisual = "complete" | "skipped" | "current" | "incomplete";

/** Hex color values for each visual state. */
export const STEP_VISUAL_COLOR: Record<StepVisual, string> = {
  complete: "#52c41a",
  skipped: "#faad14",
  current: "#1890ff",
  incomplete: "#d9d9d9",
};

/**
 * Compute the visual state of a single wizard step.
 *
 * Precedence (early-return, no nested ternary):
 *  1. Completed + required (or has data)  → "complete"
 *  2. Completed + optional + no data      → "skipped"
 *  3. Current step                        → "current"
 *  4. Everything else                     → "incomplete"
 *
 * A completed step always shows its green/amber color even when it is
 * also the current step — this preserves the existing precedence in the
 * prior `getStepColor` where completed (green) won over current (blue).
 */
export function computeStepVisual(
  index: number,
  args: {
    completedSteps: readonly number[];
    currentStep: number;
    hasData: (i: number) => boolean;
  },
): StepVisual {
  const completed = args.completedSteps.includes(index);

  if (completed && (!OPTIONAL_STEPS.has(index) || args.hasData(index))) {
    return "complete";
  }

  if (completed && OPTIONAL_STEPS.has(index) && !args.hasData(index)) {
    return "skipped";
  }

  if (index === args.currentStep) {
    return "current";
  }

  return "incomplete";
}
